import { HttpStatusCode } from '@solidjs/start';
import { useAnalytics, useTrackEvent } from '@troon/analytics';
import { createEffect, createSignal, ErrorBoundary, Index, onMount, Show, Suspense, useContext } from 'solid-js';
import {
	Button,
	Container,
	Dialog,
	DialogContent,
	Errors,
	Form,
	Heading,
	HiddenFields,
	HorizontalRule,
	Link,
	Page,
	Section,
	Grid,
	GridFive,
	GridSeven,
	ActivityIndicator,
	Receipt,
	CardDetails,
	RadioGroup,
	RadioBar,
	RadioBarButton,
	RateField,
	DialogTrigger,
	TextLink,
	Label,
	LinkButton,
	AccessCircleLogo,
} from '@troon/ui';
import { createStore, produce } from 'solid-js/store';
import { IconArrowLeftMd } from '@troon/icons/arrow-left-md';
import { IconArrowRightMd } from '@troon/icons/arrow-right-md';
import { createAsync, useAction, useLocation, useNavigate, useSubmission } from '@solidjs/router';
import { Meta, Title } from '@solidjs/meta';
import { z } from 'zod';
import { IconCircleWarning } from '@troon/icons/circle-warning';
import { cachedGet, clientAction } from '@troon/api-client';
import { coerceBoolean, createSearchStore } from '@troon/primitives/search-store';
import { useUtmParams } from '@troon/primitives/utm';
import dayjs, { dayTimeToDate } from '@troon/dayjs';
import { PaymentMethodSelector } from '@troon/payments';
import { CheckoutStep } from '../../../../components/reserve-tee-time/checkout-step';
import { useUser } from '../../../../providers/user';
import { AuthFlow } from '../../../../partials/auth/auth';
import { authHeaders } from '../../../../partials/auth/inline';
import { RequestPhoneNumber } from '../../../../components/request-phone-number';
import { Waiver } from '../../../../partials/waiver';
import { usePersisted } from '../../../../providers/persistence-store';
import { FacilityCtx } from '../../../../providers/facility';
import { getConfigValue } from '../../../../modules/config';
import { PlayersSelect } from '../../../../components/reserve-tee-time/players-select';
import { TeeTimeInfoHeader } from '../../../../components/reserve-tee-time/tee-time-info';
import { RateSelect } from '../../../../components/reserve-tee-time/rate-select';
import ConfirmSubscription from '../../../../components/confirm-subscription';
import { FrequentlyAskedQuestions } from '../../../../components/faqs';
import { RewardsPointsEarned } from '../../../../components/reserve-tee-time/rewards-points';
import { RewardsPointsWarning } from '../../../../components/reserve-tee-time/points-warning';
import { AccessOptInStep } from '../../../../components/reserve-tee-time/access-opt-in-step';
import type { WaiverHandler } from '../../../../partials/waiver';
import type { CreditCard } from '@troon/payments/golfnow';
import type { RouteDefinition, RouteSectionProps } from '@solidjs/router';

type Steps = 'rate' | 'players' | 'redemption' | 'auth' | 'access-opt-in' | 'payment' | 'phone' | 'ready';

const order: Array<Steps> = ['rate', 'players', 'redemption', 'auth', 'access-opt-in', 'payment', 'phone', 'ready'];
const stepFlow = 'Reserve Tee Time';
const stepOptions = { noDuplicates: true };

type Store = {
	current: Steps;
	steps: Record<Steps, { complete: boolean; skipped: boolean }>;
};

const schema = z.object({
	players: z.coerce.number().min(1).max(4).default(1),
	rateId: z.string().nullish(),
	triggerId: z.string().nullish(),
	// Access was purchased and redirected back to this tee time
	subscriptionId: z.string().nullish(),
	productId: z.string().nullish(),
	confirmed: coerceBoolean().nullish(),
	payMode: z.enum(['points', 'dollars']).default('dollars'),
	redeemForAll: coerceBoolean().nullish(),
});

export default function ReserveTeeTime(props: RouteSectionProps) {
	const user = useUser();
	const facility = useContext(FacilityCtx);
	const trackEvent = useTrackEvent();

	// Temporary for experiment 'booking-flow=stepped'
	const analytics = useAnalytics();
	onMount(() => {
		analytics?.startSessionRecording();
	});

	// UTM params are sent to the reservation mutation for tracking purposes
	const utm = useUtmParams();

	const [filters, setFilters] = createSearchStore(schema);

	const requiresAccessOptIn = () => {
		const userData = user();
		return userData && userData.rewardsPoints === 0 && !userData.accessRewardsOptIn;
	};

	const [store, setStore] = createStore<Store>({
		current: 'rate',
		steps: {
			rate: { complete: false, skipped: false },
			players: { complete: false, skipped: false },
			redemption: { complete: false, skipped: true },
			auth: { complete: !!user(), skipped: !!user() },
			'access-opt-in': { complete: !requiresAccessOptIn(), skipped: !requiresAccessOptIn() },
			payment: { complete: false, skipped: false },
			phone: { complete: !!user()?.phoneNumber, skipped: !!user()?.phoneNumber },
			ready: { complete: false, skipped: false },
		},
	});
	const [card, setCard] = createSignal<CreditCard>();
	const [hasPhone, setHasPhone] = createSignal(user() ? !!user()?.phoneNumber : undefined);
	const [needsAccessOptIn, setNeedsAccessOptIn] = createSignal(requiresAccessOptIn());
	const [form, setForm] = createSignal<HTMLFormElement>();
	const [waiver, setWaiver] = createSignal<WaiverHandler>();
	const [clickedNotReady, setClickedNotReady] = createSignal(false);
	const [persisted] = usePersisted();
	const navigate = useNavigate();
	const location = useLocation();

	const [confirmSubscriptionOpen, setConfirmSubscriptionOpen] = createSignal(
		!!(filters.subscriptionId && filters.productId),
	);

	const data = createAsync(
		async () => {
			const currentFilters = filters;
			const currentUtm = utm();

			return getTeeTime({
				query: {
					rateId: currentFilters.rateId,
					players: currentFilters.players,
					payMode: !facility?.supportsAccessRewards ? 'dollars' : filters.payMode,
					redeemForAll: filters.redeemForAll && filters.payMode === 'points',
					utmCampaign: currentUtm.campaign,
					utmSource: currentUtm.source,
					utmContent: currentUtm.content,
					utmMedium: currentUtm.medium,
					utmTerm: currentUtm.term,
				},
				path: { teeTimeId: props.params.teeTimeId! },
			});
		},
		{ deferStream: true },
	);

	const triggerFormAction = useAction(reserve);
	const submission = useSubmission(reserve);

	createEffect(() => {
		if (!submission.result) {
			return;
		}

		const confirmationPathname = `/reservation/${submission.result.id}/confirmed`;
		const postTrack = () => navigate(confirmationPathname);

		trackEvent('reserveTeeTime', {
			facilitySlug: facility?.slug,
			value: parseFloat(
				data.latest?.reservationInfo?.paymentInfo.receipt.totals.find((t) => t.label === 'Total')?.amount.value ?? '0',
			),
			players: submission.result.playerCount,
			courseId: submission.result.course.id,
			dayOfWeek: dayjs.tz(submission.result.teeTime.epoch, submission.result.teeTime.tz).format('dddd'),
			daysOut: dayjs
				.tz(submission.result.teeTime.epoch, submission.result.teeTime.tz)
				.startOf('day')
				.diff(dayjs().startOf('day'), 'day'),
		});

		if (persisted.rwg_token && persisted.rwg_token.expires > Date.now()) {
			const merchantChanged = facility?.slug === utm().source ? 2 : 1;
			fetch(getConfigValue('GOOGLE_ACTIONS_ENDPOINT'), {
				method: 'post',
				body: JSON.stringify({
					conversion_partner_id: getConfigValue('GOOGLE_ACTIONS_PARTNER_ID'),
					rwg_token: persisted.rwg_token.value,
					merchant_changed: `${merchantChanged}`,
				}),
			})
				// ensure it navigates regardless of fetch result
				.then(postTrack, postTrack)
				.catch(postTrack);
			return;
		}

		postTrack();
	});

	createEffect(() => {
		setStore('steps', 'auth', 'complete', !!user());
	});

	// Manage access opt-in step visibility based on user state
	createEffect(() => {
		if (user() && requiresAccessOptIn()) {
			setNeedsAccessOptIn(true);
			setStore('steps', 'access-opt-in', { complete: false, skipped: false });
		} else if (user() && !requiresAccessOptIn() && needsAccessOptIn() === undefined) {
			setStore('steps', 'access-opt-in', { complete: true, skipped: true });
			setNeedsAccessOptIn(false);
		}
	});

	// Ensures the contact information step gets set to required or not
	createEffect(() => {
		if (user() && !user()?.phoneNumber) {
			setHasPhone(false);
		} else if (user()?.phoneNumber && hasPhone() === undefined) {
			setStore('steps', 'phone', { complete: true, skipped: true });
			setHasPhone(true);
		}
	});

	// Ensure the payment information is set to required or not
	createEffect(() => {
		try {
			const required = !!data()?.reservationInfo?.paymentInfo.requiresCreditCard;
			setStore('steps', 'payment', { complete: !required || !!card(), skipped: !required });
		} catch {
			// no-op: tee time (data()) is not available
		}
	});

	// Ensure the redemption step is only required in points mode
	createEffect(() => {
		const isPointsMode = filters.payMode === 'points';
		const currentData = data();

		const hasRedemptionOptions = !!(currentData?.reservationInfo?.accessRewardsDetails?.redemptionOptions?.length ?? 0);
		const shouldShow = isPointsMode && hasRedemptionOptions && filters.players > 1;
		setStore('steps', 'redemption', {
			complete: !shouldShow || !!filters.redeemForAll,
			skipped: !shouldShow,
		});
	});

	// Ensure the player count selection is within the min/max range
	createEffect(() => {
		try {
			const max = Math.max(...(data()?.reservationInfo?.selectedRate.players ?? [4]));
			const min = Math.min(...(data()?.reservationInfo?.selectedRate.players ?? [1]));
			if (filters.players > max) {
				setFilters('players', max);
			}
			if (filters.players < min) {
				setFilters('players', min);
			}
		} catch {
			// no-op: tee time (data()) is not available
		}
	});

	createEffect(() => {
		if (store.current === 'ready' && clickedNotReady()) {
			setClickedNotReady(false);
		}
	});

	createEffect(() => {
		if (!store.steps.auth.skipped && store.steps.auth.complete) {
			const accessRate = data.latest?.reservationInfo?.rates.find((r) => r.rateType.startsWith('TROON_ACCESS'));
			if (
				user()?.troonAccessProductType &&
				data.latest?.reservationInfo?.selectedRate.rateType === 'PUBLIC' &&
				accessRate
			) {
				setFilters('rateId', accessRate.id);
				edit('rate');
				return;
			}
		}
	});

	/**
	 * Continues to the next incomplete step in the flow
	 */
	function next() {
		let start = order.indexOf(store.current);
		if (start < 0) {
			return;
		}

		for (; start < order.length - 1; start += 1) {
			const step = order[start]!;
			if (!store.steps[step].complete) {
				setStore('current', step);
				return;
			}
			trackEvent('stepCompleted', { stepFlow, stepName: step, stepSkipped: store.steps[step].skipped }, stepOptions);
		}

		setStore('current', order[order.length - 1]!);
	}

	/**
	 * Completes the current step and auto-advances to the next incomplete step in the flow
	 */
	function complete() {
		setStore(
			produce((s) => {
				s.steps[s.current].complete = true;
			}),
		);
		next();
	}

	/**
	 * Returns to the named step for editing
	 * @param step {Steps}
	 */
	function edit(step: Steps) {
		setStore({ current: step });
	}

	/**
	 * Get the step's state as a string enum
	 * @param step {Steps}
	 * @returns String enum for step state
	 */
	function stepState(step: Steps) {
		return store.current === step ? 'current' : store.steps[step].complete ? 'completed' : 'waiting';
	}

	function getIndex(step: Steps) {
		// eslint-disable-next-line solid/reactivity
		const index = order.slice(0, order.indexOf(step)).reduce((index, step) => {
			return index + (!store.steps[step].skipped ? 1 : 0);
		}, 1);
		return index;
	}

	const [canContinueWithRate, setCanContinueWithRate] = createSignal(false);

	return (
		<div class="-mb-12 min-h-svh bg-neutral-100">
			<Title>Book Tee Time | {facility!.name} | Golf with Access</Title>
			<Meta name="robots" content="noindex" />
			<Meta name="googlebot" content="noindex" />
			<Container>
				<Page class="gap-8 md:gap-12">
					<Section>
						<Heading as="h1">Book Tee Time</Heading>
					</Section>
					<ErrorBoundary fallback={null}>
						<Section appearance="contained" class="lg:hidden">
							<TeeTimeInfoHeader
								cartIncluded={data.latest?.reservationInfo?.selectedRate.cartIncluded}
								courseName={data.latest?.course.name}
								date={data.latest?.dayTime ? dayTimeToDate(data.latest?.dayTime) : undefined}
								holes={data.latest?.reservationInfo?.holes}
								players={data.latest?.reservationInfo?.players}
								facilityName={facility!.name}
								facilitySlug={facility!.slug}
								facilityLogo={facility!.logo}
							/>
						</Section>
					</ErrorBoundary>

					<Grid class="gap-y-8 lg:gap-y-8 xl:gap-y-8">
						<GridSeven class="flex flex-col gap-4">
							<ErrorBoundary
								fallback={
									<>
										<HttpStatusCode code={404} />
										<Section appearance="contained">
											<Heading as="h2">Unavailable</Heading>
											<p>This tee time is no longer available.</p>
											<Button
												as={Link}
												onClick={(e) => {
													if (history.length > 1) {
														e.preventDefault();
														navigate(-1);
														return;
													}
												}}
												href={`/course/${facility!.slug}/reserve-tee-time`}
												appearance="tertiary"
											>
												<IconArrowLeftMd /> See available Tee Times
											</Button>
										</Section>
									</>
								}
							>
								<Show when={data.latest?.reservationInfo}>
									{(reservationInfo) => (
										<>
											{/**
											 * Rate selection
											 */}
											<CheckoutStep
												state={stepState('rate')}
												step={getIndex('rate')}
												title="Choose Your Rate"
												currentAction={
													<RadioGroup
														name="payMode"
														class="w-full max-w-lg text-base md:min-w-[320px]"
														onSelect={(value) => setFilters('payMode', value as 'dollars' | 'points')}
													>
														<RadioBar>
															<RadioBarButton value="dollars" checked={filters.payMode === 'dollars'}>
																<Label class="px-2">Dollars</Label>
															</RadioBarButton>
															<RadioBarButton value="points" checked={filters.payMode === 'points'}>
																<Label class="px-2">Points</Label>
															</RadioBarButton>
														</RadioBar>
													</RadioGroup>
												}
												onEdit={() => edit('rate')}
												value={`${reservationInfo().selectedRate.name} ${reservationInfo().selectedRate.description ? ` • ${reservationInfo().selectedRate.description}` : reservationInfo().selectedRate.rateType === 'PUBLIC' ? ' • Standard rate' : ''}`}
											>
												<Show
													when={filters.payMode === 'points' && !facility?.supportsAccessRewards}
													fallback={
														<>
															<RateSelect
																rates={reservationInfo().rates}
																onSelect={(rateId, allowed) => {
																	setFilters('rateId', rateId);
																	setCanContinueWithRate(allowed);
																}}
																selected={filters.rateId || reservationInfo().selectedRate.id}
																payMode={filters.payMode}
																onPayModeChange={(mode) => setFilters('payMode', mode)}
																supportsAccessPoints={facility?.supportsAccessRewards ?? false}
															/>

															<Button
																onClick={complete}
																disabled={filters.payMode !== 'dollars' && !canContinueWithRate()}
															>
																Continue<span class="sr-only"> with this rate</span>
															</Button>
														</>
													}
												>
													<div class="flex flex-col items-center justify-center gap-6 rounded-lg p-8 text-center">
														<AccessCircleLogo class="size-16 text-neutral-400" color="silver" />
														<p class="text-sm text-neutral-800">
															This course does not participate in the Access program yet.
														</p>
														<Button appearance="secondary" onClick={() => setFilters('payMode', 'dollars')}>
															View available rates
														</Button>
													</div>
												</Show>
											</CheckoutStep>

											{/**
											 * Player select
											 */}
											<CheckoutStep
												state={stepState('players')}
												step={getIndex('players')}
												title="Number of Players"
												onEdit={() => edit('players')}
												value={`${reservationInfo().players} player${(reservationInfo().players ?? 1) > 1 ? 's' : ''}`}
											>
												<Suspense>
													<PlayersSelect
														maxPlayers={Math.max(...(data.latest?.reservationInfo?.selectedRate.players ?? [4]))}
														minPlayers={Math.min(...(data.latest?.reservationInfo?.selectedRate.players ?? [1]))}
														rateName={data()?.reservationInfo?.selectedRate.name}
														players={filters.players}
														setPlayers={(count: number) => setFilters('players', count)}
														earnsAccessPoints={!!(facility?.supportsAccessRewards && filters.payMode === 'dollars')}
													/>
												</Suspense>

												<Button onClick={complete} disabled={filters.players !== data.latest?.reservationInfo?.players}>
													Continue<span class="sr-only"> with {filters.players} players</span>
												</Button>
											</CheckoutStep>

											{/**
											 * Book with Points - Redemption options
											 */}
											<Show
												when={
													!store.steps.redemption.skipped && reservationInfo().accessRewardsDetails?.redemptionOptions
												}
											>
												{(options) => (
													<CheckoutStep
														state={stepState('redemption')}
														step={getIndex('redemption')}
														title="Book with Points"
														onEdit={() => edit('redemption')}
														value={filters.redeemForAll ? 'Entire group' : 'Just myself'}
													>
														<p>Select who you want to redeem points for on this tee time:</p>
														<RadioGroup name="redemption" required class="gap-4">
															<Label class="sr-only">Choose redemption option</Label>
															<Index each={options()}>
																{(option) => (
																	<RateField
																		onChange={(e) => {
																			if (e.target.checked) {
																				setFilters('redeemForAll', option().redeemForAll);
																			}
																		}}
																		value={String(option().redeemForAll)}
																		publicPrice={option().price}
																		description={option().description}
																		price={option().price}
																		name={option().name}
																		rateType={option().rateType}
																		discountAmount=""
																		discountPercent={0}
																		checked={option().redeemForAll === filters.redeemForAll}
																		disabled={!!option().disabled}
																	></RateField>
																)}
															</Index>
														</RadioGroup>
														<Show
															when={
																data.latest?.reservationInfo?.accessRewardsDetails?.remainingPoints !== undefined &&
																(data.latest?.reservationInfo?.accessRewardsDetails?.remainingPoints ?? 0) < 0
															}
															fallback={
																<Show when={data.latest?.reservationInfo?.accessRewardsDetails?.remainingPoints}>
																	{(remaining) => (
																		<p class="mt-2 text-sm text-brand-700">
																			You'll have <b>{remaining().toLocaleString()} pts remaining</b> after booking this
																			reservation with points.
																		</p>
																	)}
																</Show>
															}
														>
															<p class="mt-2 text-sm text-red-600">
																You don't have enough points to book this reservation. You need{' '}
																<b>
																	{Math.abs(
																		data.latest?.reservationInfo?.accessRewardsDetails?.remainingPoints ?? 0,
																	).toLocaleString()}{' '}
																	more pts
																</b>
																.
															</p>
														</Show>

														<Button
															onClick={complete}
															disabled={
																data.latest?.reservationInfo?.accessRewardsDetails?.remainingPoints !== undefined &&
																(data.latest?.reservationInfo?.accessRewardsDetails?.remainingPoints ?? 0) < 0
															}
														>
															Continue<span class="sr-only"> with selected redemption option</span>
														</Button>
													</CheckoutStep>
												)}
											</Show>

											{/**
											 * Sign in
											 */}
											<Show when={!store.steps.auth.skipped}>
												<CheckoutStep
													state={stepState('auth')}
													step={getIndex('auth')}
													title="Log in or Sign up"
													value={
														user() ? (
															<div>
																<p>
																	{user()?.firstName} {user()?.lastName}
																</p>
																<p class="truncate">{user()?.email}</p>
																<p>Rewards #{user()?.troonRewardsId}</p>
															</div>
														) : null
													}
												>
													<Show when={!user()}>
														<AuthFlow inline onComplete={() => complete()} headers={authHeaders} showSteps={false} />
													</Show>
												</CheckoutStep>
											</Show>

											{/**
											 * Access Opt-In
											 */}
											<Show when={needsAccessOptIn()}>
												<CheckoutStep
													state={stepState('access-opt-in')}
													step={getIndex('access-opt-in')}
													title="Join Access"
													value={store.steps['access-opt-in'].complete ? 'Access member' : ''}
												>
													<AccessOptInStep onComplete={complete} />
												</CheckoutStep>
											</Show>

											{/**
											 * Payment Method / Credit Card
											 */}
											<Show when={!store.steps.payment.skipped}>
												<CheckoutStep
													state={stepState('payment')}
													step={getIndex('payment')}
													title="Payment Method"
													onEdit={() => edit('payment')}
													value={
														<Show when={card()}>
															{(card) => <CardDetails brand={card().brand} lastFour={card().lastFour} />}
														</Show>
													}
												>
													<Show when={user() && data.latest?.reservationInfo?.paymentInfo.paymentProcessor}>
														{(processor) => (
															<PaymentMethodSelector
																labelHidden
																processor={processor()}
																onComplete={(card) => {
																	setCard(card);
																	complete();
																}}
																onSelect={(card) => {
																	if (store.current === 'payment') {
																		setCard(card);
																	}
																}}
																stripePublishableKey={getConfigValue('STRIPE_PUBLISHABLE_KEY')}
																wallets={{ applePay: 'never', googlePay: 'never', link: 'never' }}
															/>
														)}
													</Show>
												</CheckoutStep>
											</Show>

											{/**
											 * Contact Information / Phone number input
											 */}
											<Show when={!hasPhone()}>
												<CheckoutStep
													state={stepState('phone')}
													step={getIndex('phone')}
													title="Contact Information"
													onEdit={() => edit('phone')}
													value={<Show when={user()}>{(me) => <p>{me().phoneNumber}</p>}</Show>}
												>
													<RequestPhoneNumber
														buttonLabel="Continue"
														onCancel={user()?.phoneNumber ? () => complete() : undefined}
														onSuccess={complete}
													/>
												</CheckoutStep>
											</Show>
										</>
									)}
								</Show>
							</ErrorBoundary>
						</GridSeven>

						<ErrorBoundary fallback={null}>
							<GridFive>
								<Section
									appearance="contained"
									class="sticky top-8 max-h-[calc(100dvh-4rem)] overflow-y-auto px-0 md:px-0"
								>
									<div class="hidden px-4 md:px-8 lg:block">
										<TeeTimeInfoHeader
											cartIncluded={data()?.reservationInfo?.selectedRate.cartIncluded}
											courseName={data.latest?.course.name}
											date={data.latest?.dayTime ? dayTimeToDate(data.latest!.dayTime) : undefined}
											holes={data()?.reservationInfo?.holes}
											players={data()?.reservationInfo?.players}
											facilityName={facility!.name}
											facilitySlug={facility!.slug}
											facilityLogo={facility!.logo}
											showInfo={!!data.latest?.reservationInfo}
										/>
									</div>
									<Show when={data.latest?.reservationInfo}>
										<div class="hidden lg:block">
											<HorizontalRule />
										</div>

										<div class="flex flex-col gap-4 px-4 md:px-8">
											<Heading as="h2" size="h5">
												Payment info
											</Heading>

											<Suspense fallback={<ActivityIndicator />}>
												<Show when={data()?.reservationInfo?.paymentInfo?.receipt}>
													{(receipt) => (
														<Receipt
															items={receipt().items.map((i) => ({ label: i.label, amount: i.displayAmount }))}
															totals={receipt().totals.map((i) => ({ label: i.label, amount: i.amount.displayValue }))}
															totalDisclaimer="All amounts displayed are subject to additional taxes and fees, which may be added at check in."
															disclaimer={receipt().disclaimer || undefined}
															reimbursement={
																receipt().reimbursement
																	? {
																			label: 'Reimbursed at check-in',
																			amount: receipt().reimbursement!.amount?.displayValue,
																			disclaimer: receipt().reimbursement!.disclaimer,
																		}
																	: undefined
															}
														/>
													)}
												</Show>
												<Show
													when={
														!user()?.accessRewardsOptIn &&
														user() &&
														(user()?.rewardsPoints ?? 0) > 0 &&
														facility?.supportsAccessRewards &&
														data()?.reservationInfo?.paymentInfo?.rewardsInfo.rewardPointsEarned &&
														stepState('rate') !== 'current'
													}
												>
													<RewardsPointsWarning>
														<p class="text-sm">
															You need to transfer your Troon Rewards points to Access to be able to earn and redeem
															points at this course.
														</p>
														<LinkButton
															href="/account/transfer-points"
															state={{ redirect: `${location.pathname}${location.search}` }}
															appearance="current"
															class="-ml-2 w-fit text-sm text-black"
														>
															Transfer Points
															<IconArrowRightMd />
														</LinkButton>
													</RewardsPointsWarning>
												</Show>
												<Show
													when={
														user()?.accessRewardsOptIn &&
														data()?.reservationInfo?.paymentInfo?.rewardsInfo.accessPointsEarned
													}
												>
													{(points) => (
														<RewardsPointsEarned
															rewardPointsEarned={points()}
															supportedRewards={facility?.supportsTroonRewards ?? false}
														/>
													)}
												</Show>
												<Show when={!facility?.supportsAccessRewards}>
													<RewardsPointsEarned rewardPointsEarned={1} supportedRewards={false} />
												</Show>
											</Suspense>
										</div>
									</Show>
								</Section>
							</GridFive>
						</ErrorBoundary>

						<ErrorBoundary fallback={null}>
							<GridSeven class="flex flex-col gap-4">
								<Section>
									<Show when={data.latest?.reservationInfo?.courseNotes}>
										{(notes) => (
											<div class="flex flex-col gap-2">
												<Heading as="h2" size="h5">
													Notes from the Course
												</Heading>
												<p>{notes()}</p>
											</div>
										)}
									</Show>

									<Show when={data.latest?.reservationInfo?.cancellationInfo.displayMessage}>
										{(cancellationPolicy) => (
											<div class="flex flex-col gap-2">
												<Heading as="h2" size="h5">
													Cancellation Policy
												</Heading>
												<p>{cancellationPolicy()}</p>
											</div>
										)}
									</Show>

									<Waiver ref={setWaiver} />

									<Show when={data.latest}>
										<Form action={reserve} ref={setForm} class="relative">
											<HiddenFields
												data={{
													reserveId: data.latest?.reservationInfo?.reserveId,
													creditCardId: card()?.id,
													...Object.entries(utm()).reduce(
														(memo, [key, val]) => ({
															...memo,
															[`utm${key.charAt(0).toUpperCase()}${key.slice(1)}`]: val,
														}),
														{} as Record<string, string | undefined>,
													),
												}}
											/>
											<Button
												type="submit"
												disabled={store.current !== 'ready'}
												onClick={(e) => {
													e.preventDefault();
													function complete() {
														const reserveData = new FormData(form());
														triggerFormAction(reserveData);
													}

													waiver()?.confirm(complete);
												}}
											>
												Book Tee Time <IconArrowRightMd />
											</Button>
											<Show when={store.current !== 'ready'}>
												<div
													class="absolute inset-0 cursor-not-allowed"
													onClick={() => {
														if (store.current !== 'ready') {
															setClickedNotReady(true);
														}
													}}
												/>
											</Show>
											<Show when={clickedNotReady()}>
												<p
													aria-live="assertive"
													class="flex items-center justify-center gap-1 font-semibold text-red-500"
												>
													<IconCircleWarning /> Please complete all steps to continue booking.
												</p>
											</Show>
											<Errors />
										</Form>
										<p>
											Questions about Access rates or points? Check our{' '}
											<Dialog key="booking-faqs">
												<DialogTrigger as={TextLink} target="_blank" href="/about/booking-faqs">
													Booking Frequently asked Questions
												</DialogTrigger>
												<DialogContent header="Booking FAQs" headerLevel="h2">
													<FrequentlyAskedQuestions id="access-rewards-faq-list" />
												</DialogContent>
											</Dialog>
											.
										</p>
									</Show>
								</Section>
							</GridSeven>
						</ErrorBoundary>
					</Grid>
				</Page>
			</Container>

			<Show when={filters.subscriptionId && filters.productId && user()}>
				<Dialog
					key="subscription-confirmation"
					defaultOpen
					open={confirmSubscriptionOpen()}
					onOpenChange={setConfirmSubscriptionOpen}
				>
					<DialogContent>
						<ConfirmSubscription
							subscriptionId={filters.subscriptionId!}
							productId={filters.productId!}
							onContinue={() => {
								setConfirmSubscriptionOpen(false);
							}}
							continueText="Continue booking"
						/>
					</DialogContent>
				</Dialog>
			</Show>
		</div>
	);
}

const getTeeTime = cachedGet('/v1/tee-times/{teeTimeId}');

const reserve = clientAction('post', '/v0/reservations', {}, { revalidate: ['/v0/auth/me'] });

export const route = {
	info: { nav: { appearance: 'minimal', sticky: false }, banner: { hide: true } },
} satisfies RouteDefinition;
